/**
 * Section Registry — Heart of Science v2
 * Maps each tab to its completable sections
 * Used for action-based section completion tracking
 * Sprint 8 — S8-24
 */

export interface SectionDef {
  id: string
  label: string
}

/**
 * Registry of sections per tab
 * Section IDs are stable identifiers stored in DB — do not rename
 * Labels are display names shown in tooltips
 */
export const SECTION_REGISTRY: Record<string, SectionDef[]> = {
  // INBOX
  'tab-quick-entry': [
    { id: 'qe-domain-source', label: 'Domain & Source' },
    { id: 'qe-quick-identification', label: 'Quick Identification' },
    { id: 'qe-minimal-team', label: 'Minimal Project Team' },
  ],

  // LONG LIST
  'tab-overview': [
    { id: 'ov-core-identification', label: 'Core Identification' },
    { id: 'ov-classification', label: 'Classification' },
    { id: 'ov-market-context', label: 'Market Context' },
    { id: 'ov-regulatory', label: 'Regulatory Landscape' },
    { id: 'ov-external-links', label: 'External Links' },
  ],

  // IN REVIEW
  'tab-team': [
    { id: 'tm-members', label: 'Team Members' },
    { id: 'tm-assessment', label: 'Team Assessment' },
    { id: 'tm-assessment-summary', label: 'Team Assessment Summary' },
  ],
  'tab-ip': [
    { id: 'ip-core-knowhow', label: 'Core Know-How' },
    { id: 'ip-protection', label: 'IP Protection' },
    { id: 'ip-fto', label: 'Freedom to Operate' },
    { id: 'ip-transfer-path', label: 'IP Transfer Path' },
  ],
  'tab-technology': [
    { id: 'tech-ai-enrichment', label: 'AI Enrichment' },
    { id: 'tech-target-profile', label: 'Target Profile' },
    { id: 'tech-external-ids', label: 'External Database IDs' },
    { id: 'tech-therapeutic', label: 'Therapeutic Classification' },
    { id: 'tech-mechanism', label: 'Target & Mechanism' },
    { id: 'tech-evidence', label: 'AI-Assessed Evidence' },
  ],
  'tab-devphase': [
    { id: 'dev-timeline', label: 'Development Phase Timeline' },
    { id: 'dev-details', label: 'Phase Details' },
    { id: 'dev-data', label: 'Development Data' },
  ],
  'tab-competition': [
    { id: 'comp-competitors', label: 'Competitors' },
    { id: 'comp-landscape-summary', label: 'Competitive Landscape Summary' },
  ],
  'tab-funding': [
    { id: 'fund-total', label: 'Total Funding' },
    { id: 'fund-grants', label: 'Grants' },
    { id: 'fund-requirements', label: 'Funder Requirements & Restrictions' },
  ],
  'tab-publications': [
    { id: 'pub-publications', label: 'Publications' },
    { id: 'pub-conferences', label: 'Related Conferences & Events' },
  ],
  'tab-defensibility': [
    { id: 'def-7powers', label: '7 Powers Assessment' },
  ],
  'tab-voting': [
    { id: 'vote-ida-review', label: 'IDA Review' },
  ],

  // EXTRA MILE
  'tab-outreach': [
    { id: 'out-events', label: 'Outreach Events' },
  ],
  'tab-irl': [
    { id: 'irl-assessment', label: 'IRL Assessment' },
  ],
  'tab-scoring': [
    { id: 'score-composite', label: 'Composite Scoring' },
  ],
  'tab-vb-recommendation': [
    { id: 'vbrec-recommendation', label: 'VB Recommendation' },
  ],

  // VB PROCESS
  'tab-vbteam': [
    { id: 'vbt-team', label: 'VB Team' },
  ],
  'tab-termsheet': [
    { id: 'ts-terms', label: 'Term Sheet' },
  ],
  'tab-venturebuilding': [
    { id: 'vb-building', label: 'Venture Building' },
  ],
  'tab-fundraising': [
    { id: 'fr-readiness', label: 'Fundraising Readiness' },
  ],

  // FUNDED
  'tab-funded': [
    { id: 'funded-status', label: 'Funded Status' },
  ],

  // Documents (ALL stages) — no completable sections
  'tab-documents': [],
}

/**
 * Get sections for a given tab
 */
export function getTabSections(tabId: string): SectionDef[] {
  return SECTION_REGISTRY[tabId] || []
}

/**
 * Get all section IDs for a tab
 */
export function getTabSectionIds(tabId: string): string[] {
  return (SECTION_REGISTRY[tabId] || []).map(s => s.id)
}
